import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import type { CalendarView } from './CalendarToolbar';
import type { CalendarEvent } from '../../types/integrations';

const WEEK_OPTIONS = { weekStartsOn: 1 as const }; // Monday-first

export const getViewRange = (
  view: CalendarView,
  cursor: Date
): { from: Date; to: Date } => {
  switch (view) {
    case 'month': {
      const monthStart = startOfMonth(cursor);
      const monthEnd = endOfMonth(cursor);
      return {
        from: startOfWeek(monthStart, WEEK_OPTIONS),
        to: endOfWeek(monthEnd, WEEK_OPTIONS),
      };
    }
    case 'week':
      return {
        from: startOfWeek(cursor, WEEK_OPTIONS),
        to: endOfWeek(cursor, WEEK_OPTIONS),
      };
    case 'day':
      return { from: startOfDay(cursor), to: endOfDay(cursor) };
    case 'agenda':
      return { from: startOfDay(cursor), to: endOfDay(addDays(cursor, 13)) };
    default:
      return { from: startOfDay(cursor), to: endOfDay(cursor) };
  }
};

export const navigateCursor = (
  view: CalendarView,
  cursor: Date,
  direction: -1 | 0 | 1
): Date => {
  if (direction === 0) return new Date();
  switch (view) {
    case 'month':
      return addMonths(cursor, direction);
    case 'week':
      return addWeeks(cursor, direction);
    case 'day':
      return addDays(cursor, direction);
    case 'agenda':
      return addDays(cursor, direction * 14);
    default:
      return cursor;
  }
};

export const safeParse = (iso: string): Date => {
  // Backend may return YYYY-MM-DD for all-day events
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return parseISO(`${iso}T00:00:00`);
  }
  return parseISO(iso);
};

export const eventsByDay = (
  events: CalendarEvent[]
): Map<string, CalendarEvent[]> => {
  const map = new Map<string, CalendarEvent[]>();
  if (!Array.isArray(events)) return map;
  for (const event of events) {
    const start = safeParse(event.start);
    const end = safeParse(event.end);
    let cursor = startOfDay(start);
    const last = startOfDay(end);
    while (cursor.getTime() <= last.getTime()) {
      const key = cursor.toISOString().slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
      cursor = addDays(cursor, 1);
    }
  }
  // Sort by start time within each day
  for (const list of map.values()) {
    list.sort(
      (a, b) => safeParse(a.start).getTime() - safeParse(b.start).getTime()
    );
  }
  return map;
};

export const dayKey = (date: Date): string =>
  startOfDay(date).toISOString().slice(0, 10);
